import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import { ADMIN_AUTH_EXPIRED_EVENT, AdminTokenStore } from './AdminTokenStore';

export interface AdminAuthContextValue {
  isAdminAuthenticated: boolean;
  adminLogin: (platformAdminKey: string) => Promise<void>;
  adminLogout: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function resolveBaseUrl(): string {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_API_BASE_URL?.trim() ?? '';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

function base64UrlDecode(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (padded.length % 4)) % 4;
  return atob(padded + '='.repeat(padding));
}

function parseScopesClaim(raw: unknown): string[] | null {
  if (Array.isArray(raw)) {
    return raw.every((x) => typeof x === 'string') ? (raw as string[]) : null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
        return parsed as string[];
      }
    } catch { /* */ }
  }
  return null;
}

function decodeAdminToken(jwt: string): { exp: number } | null {
  const parts = jwt.split('.');
  if (parts.length !== 3 || parts[1] === undefined) return null;
  try {
    const raw = JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>;
    const exp = raw['exp'];
    const scopes = parseScopesClaim(raw['scopes']);
    if (typeof exp !== 'number' || scopes === null) return null;
    if (!scopes.includes('platform:admin')) return null;
    return { exp };
  } catch {
    return null;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(
    () => AdminTokenStore.isValid(),
  );
  const pushToast = useToastStore((state) => state.push);

  const adminLogout = useCallback(() => {
    AdminTokenStore.clear();
    setIsAdminAuthenticated(false);
  }, []);

  const adminLogin = useCallback(async (platformAdminKey: string) => {
    const base = resolveBaseUrl();
    const response = await fetch(`${base}/api/v1/admin/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ platformAdminKey }),
    });
    if (!response.ok) {
      throw new Error('Credenciales inválidas.');
    }
    const data = (await response.json()) as { accessToken: string; expiresIn: number };
    const decoded = decodeAdminToken(data.accessToken);
    if (decoded === null) {
      throw new Error('Token de plataforma inválido.');
    }
    AdminTokenStore.set({ accessToken: data.accessToken, expiresAtMs: decoded.exp * 1000 });
    setIsAdminAuthenticated(true);
  }, []);

  useEffect(() => {
    const handler = (): void => {
      pushToast({
        tone: 'warning',
        title: 'Sesión de admin expirada',
        description: 'Vuelve a iniciar sesión como administrador de plataforma.',
      });
      adminLogout();
    };
    window.addEventListener(ADMIN_AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_AUTH_EXPIRED_EVENT, handler);
  }, [adminLogout, pushToast]);

  const value = useMemo(
    () => ({ isAdminAuthenticated, adminLogin, adminLogout }),
    [isAdminAuthenticated, adminLogin, adminLogout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
